import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invitation, InvitationList, TenantAdminList } from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  ListItem,
  PageHead,
  PersonCell,
  TabPanel,
  Tabs,
  Table,
  useAdminToast,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import '../organisatie/organisatie.css';
import { formatDate, formatDateTime, localized } from '../../shared/format';
import { ListError } from '../../shared/list-error';
import { invitationToast } from '../../shared/invitation-toast';
import { useSession } from '../../shared/session';
import { EmployeeInvitationsTab, useEmployeeInvitations } from './employee-invitations';
import { InviteDialog } from './invitations-page';

type Tab = 'admins' | 'employees';

/**
 * Uitnodigingen: wie er beheerder is, en wie er een uitnodiging in zijn mailbox heeft (Extra 4;
 * ontwerp O8 en O9).
 *
 * Twee tabbladen: Beheerders en Medewerkers (Stap 34, REG-2). De beheerders blijven het eerste
 * tabblad: wie dit scherm al kende, vindt het op dezelfde plek. De knop in de paginakop hoort bij
 * het tabblad dat open staat.
 *
 * De twee lijsten van Beheerders staan onder elkaar en niet naast elkaar: ze beantwoorden dezelfde
 * vraag in volgorde van zekerheid. Wie er binnen is, staat bovenaan; wie er misschien binnenkomt,
 * eronder.
 *
 * Het venster "Beheerder uitnodigen" is `InviteDialog` uit `invitations-page.tsx`: het
 * medewerkersscherm gebruikt het ook (M4 in deel 02), en het verandert daar.
 */
export function InvitationsPage() {
  const { t } = useTranslation();
  const { has } = useSession();
  const [uitnodigen, setUitnodigen] = useState(false);
  const [nieuweUitnodiging, setNieuweUitnodiging] = useState(false);
  const [tab, setTab] = useState<Tab>('admins');

  const kanUitnodigen = has('identity.invitation.create');
  // De teller op de tab: de uitnodigingen die nog werken (O8 "Medewerkers 3").
  const medewerkers = useEmployeeInvitations();
  const actief = (medewerkers.data ?? []).filter((item) => item.status === 'open').length;

  return (
    <AdminPage>
      <PageHead
        title={t('invitations.title')}
        actions={
          !kanUitnodigen ? null : tab === 'admins' ? (
            <Btn
              icon={<AdminIcon name="plus" />}
              testId="invite-admin"
              onClick={() => setUitnodigen(true)}
            >
              {t('invitations.invite')}
            </Btn>
          ) : (
            <Btn
              icon={<AdminIcon name="plus" />}
              testId="employee-invitation-new"
              onClick={() => setNieuweUitnodiging(true)}
            >
              {t('employeeInvitations.new')}
            </Btn>
          )
        }
      />

      <Tabs
        label={t('invitations.title')}
        idPrefix="uitnodigingen"
        value={tab}
        onChange={setTab}
        tabs={[
          {
            key: 'admins',
            label: t('invitations.tabAdmins'),
            testId: 'invitations-tab-admins',
          },
          {
            key: 'employees',
            label: t('invitations.tabEmployees'),
            count: medewerkers.data ? actief : undefined,
            testId: 'invitations-tab-employees',
          },
        ]}
      />

      <TabPanel idPrefix="uitnodigingen" tabKey={tab} className="a-stack">
        {tab === 'admins' ? (
          <>
            <AdminsCard />
            <InvitationsCard />
          </>
        ) : (
          <EmployeeInvitationsTab
            creating={nieuweUitnodiging}
            onCreatingChange={setNieuweUitnodiging}
          />
        )}
      </TabPanel>

      <InviteDialog open={uitnodigen} onOpenChange={setUitnodigen} />
    </AdminPage>
  );
}

/** Wie er nu beheerder is: iemand met een rol waarin "rollen beheren" zit. */
function AdminsCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const admins = useQuery({
    queryKey: ['identity', 'admins'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/invitations/admins');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as TenantAdminList).items;
    },
  });

  return (
    <Card testId="admins-card">
      <CardHead title={t('invitations.currentAdmins')} count={admins.data?.length} />

      {admins.isPending ? (
        <CardPad>
          <LoadingState rows={3} label={t('common.loading')} />
        </CardPad>
      ) : admins.isError ? (
        <CardPad>
          <ListError onRetry={() => void admins.refetch()} error={admins.error} />
        </CardPad>
      ) : (admins.data?.length ?? 0) === 0 ? (
        <Empty
          icon={<AdminIcon name="key" size={24} />}
          title={t('invitations.noAdmins')}
          text={t('invitations.currentAdminsHint')}
        />
      ) : (
        <Table label={t('invitations.currentAdmins')}>
          <thead>
            <tr>
              <th>{t('people.name')}</th>
              <th>{t('login.email')}</th>
              <th>{t('roles.title')}</th>
              <th>{t('invitations.lastLogin')}</th>
            </tr>
          </thead>
          <tbody>
            {admins.data?.map((admin) => (
              <tr key={admin.personId} data-testid="admin-row">
                <td>
                  <PersonCell name={admin.displayName} />
                </td>
                <td>{admin.email ?? '—'}</td>
                <td>
                  <div className="a-actions">
                    {/* In de taal van de lezer; `roles` is de Nederlandse terugval. */}
                    {admin.roles.map((role, index) => (
                      <Badge key={role} tone="navy">
                        {localized(admin.roleNames?.[index]) ?? role}
                      </Badge>
                    ))}
                  </div>
                </td>
                {/*
                  Een streepje en geen "nooit": iemand die net uitgenodigd is en nog niet
                  aanmeldde, hoort niet te lezen als iemand die het al maanden laat liggen.
                */}
                <td>{admin.lastLoginAt ? formatDateTime(admin.lastLoginAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** Openstaande uitnodigingen als lijstitems (O8), met de afgehandelde ingeklapt eronder. */
function InvitationsCard() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const queryClient = useQueryClient();
  const [intrekken, setIntrekken] = useState<Invitation | null>(null);
  const [toonAfgehandeld, setToonAfgehandeld] = useState(false);

  const invitations = useQuery({
    queryKey: ['identity', 'invitations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/invitations', {
        params: { query: { kind: 'admin' as const } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as InvitationList).items;
    },
  });

  const resend = useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await api.POST('/api/v1/invitations/{invitationId}/resend', {
        params: { path: { invitationId } },
      });
      if (error) throw error;
      return data as Invitation | undefined;
    },
    onSuccess: async (invitation) => {
      await queryClient.invalidateQueries({ queryKey: ['identity', 'invitations'] });
      // Een nieuwe link is gemaakt, maar is de mail ook vertrokken? Niet groen als dat niet zo is.
      const uitkomst = invitationToast(invitation, true);
      if (uitkomst.failed) toast.show({ title: t(uitkomst.key), tone: 'danger' });
      else adminToast.show(t(uitkomst.key));
    },
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await api.POST('/api/v1/invitations/{invitationId}/revoke', {
        params: { path: { invitationId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setIntrekken(null);
      await queryClient.invalidateQueries({ queryKey: ['identity', 'invitations'] });
      adminToast.show(t('invitations.revokedToast'));
    },
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  const alles = invitations.data ?? [];
  const open = alles.filter((item) => item.status === 'open');
  const afgehandeld = alles.filter((item) => item.status !== 'open');
  const magOpnieuw = has('identity.invitation.create');
  const magIntrekken = has('identity.invitation.revoke');

  // De rolnaam in de taal van de lezer; `roleName` alleen als een oudere server geen talen meegeeft.
  const rol = (item: Invitation) =>
    localized(item.roleNames) ?? item.roleName ?? t('invitations.roleAdmin');
  const door = (item: Invitation) =>
    item.invitedByName ??
    t(item.invitedByKind === 'platform' ? 'invitations.byPlatform' : 'invitations.byTenant');

  return (
    <Card testId="invitations-card">
      <CardHead
        title={t('invitations.pending')}
        count={invitations.data ? open.length : undefined}
      />

      {invitations.isPending ? (
        <CardPad>
          <LoadingState rows={3} label={t('common.loading')} />
        </CardPad>
      ) : invitations.isError ? (
        <CardPad>
          <ListError onRetry={() => void invitations.refetch()} error={invitations.error} />
        </CardPad>
      ) : open.length === 0 ? (
        /*
          De lege toestand legt uit wat een beheerder is.

          "Geen openstaande uitnodigingen" zou kloppen en niets zeggen. Wie hier voor het eerst
          komt, vraagt zich af wat hij iemand precies geeft - en dat hoort hier te staan en niet
          in een handleiding.
        */
        <Empty
          icon={<AdminIcon name="mail" size={24} />}
          title={t('invitations.emptyTitle')}
          text={t('invitations.emptyHint')}
        />
      ) : (
        open.map((item) => {
          const mislukt = item.mailStatus === 'failed';
          return (
            <ListItem
              key={item.id}
              testId="invitation-row"
              tile={
                mislukt ? <AdminIcon name="alert" size={18} /> : <AdminIcon name="mail" size={18} />
              }
              tileTone={mislukt ? 'danger' : 'blue'}
              title={item.email}
              meta={`${item.displayName} · ${
                mislukt
                  ? t('beheer.organisatie.invitations.mailFailedMeta', { role: rol(item) })
                  : t('beheer.organisatie.invitations.sentMeta', {
                      role: rol(item),
                      when: formatDateTime(item.createdAt),
                      by: door(item),
                      until: formatDate(item.expiresAt),
                    })
              }`}
              action={
                <div className="a-actions" style={{ flexWrap: 'nowrap' }}>
                  {mislukt ? (
                    <Badge tone="danger" testId="invitation-mail-failed">
                      {t('invitations.mailFailed')}
                    </Badge>
                  ) : (
                    <Badge tone="info">{t('beheer.organisatie.invitations.sent')}</Badge>
                  )}
                  {magOpnieuw ? (
                    <Btn
                      variant={mislukt ? 'sec' : 'ghost'}
                      size="sm"
                      icon={mislukt ? <AdminIcon name="rotate" /> : undefined}
                      testId="invitation-resend"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(item.id)}
                    >
                      {mislukt ? t('invitations.retry') : t('invitations.resend')}
                    </Btn>
                  ) : null}
                  {magIntrekken ? (
                    <Btn
                      variant="ghost"
                      size="sm"
                      testId="invitation-revoke"
                      onClick={() => setIntrekken(item)}
                    >
                      {t('invitations.revoke')}
                    </Btn>
                  ) : null}
                </div>
              }
            />
          );
        })
      )}

      {/*
        Verlopen, ingetrokken en aanvaarde uitnodigingen blijven dertig dagen zichtbaar, ingeklapt.

        Ze horen niet tussen de openstaande: dat zijn de rijen waar nog iets mee moet. Maar ze
        weggooien zou de vraag "wie hebben we ooit uitgenodigd" onbeantwoordbaar maken.
      */}
      {afgehandeld.length > 0 ? (
        <div data-testid="invitations-handled">
          <button
            type="button"
            className="a-item"
            aria-expanded={toonAfgehandeld}
            aria-controls="uitnodigingen-afgehandeld"
            onClick={() => setToonAfgehandeld((huidig) => !huidig)}
          >
            <div className="a-item-info">
              <div className="a-name a-muted">
                {t('invitations.handled', { count: afgehandeld.length })}
              </div>
            </div>
            {toonAfgehandeld ? <AdminIcon name="chevronUp" /> : <AdminIcon name="chevronDown" />}
          </button>
          {toonAfgehandeld ? (
            <div id="uitnodigingen-afgehandeld">
              <Table label={t('invitations.handled', { count: afgehandeld.length })}>
                <thead>
                  <tr>
                    <th>{t('people.name')}</th>
                    <th>{t('login.email')}</th>
                    <th>{t('posts.status')}</th>
                    <th>{t('invitations.expires')}</th>
                  </tr>
                </thead>
                <tbody>
                  {afgehandeld.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="a-name">{item.displayName}</span>
                      </td>
                      <td>{item.email}</td>
                      <td>
                        <Badge
                          tone={
                            item.status === 'accepted'
                              ? 'ok'
                              : item.status === 'revoked'
                                ? 'danger'
                                : 'neutral'
                          }
                        >
                          {t(`invitations.status.${item.status}`)}
                        </Badge>
                      </td>
                      <td>{formatDateTime(item.expiresAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmModal
        open={intrekken !== null}
        onClose={() => setIntrekken(null)}
        title={t('invitations.revokeTitle')}
        description={t('invitations.revokeConfirm', { email: intrekken?.email ?? '' })}
        confirmLabel={t('invitations.revoke')}
        busy={revoke.isPending}
        testId="confirm-revoke"
        onConfirm={() => intrekken && revoke.mutate(intrekken.id)}
      />

      {adminToast.element}
    </Card>
  );
}
